/**
 * Assessment and Assessment Item Types
 */

import type { EvidenceStatus } from './evidence';

// Assessment types - single assessor assignment
export interface Assessment {
  assessment_id: number;
  assessment_name: string;
  assessment_status: AssessmentStatus;
  scheduled_date: string | null;
  completed_date: string | null;
  evidence_due_date?: string | null;
  publish_date?: string | null;
  cm_final_response_date?: string | null;
  created_at: string;
  updated_at: string;
  assessment_notes: string;
  high_gaps: number;
  medium_gaps: number;
  low_gaps: number;
  total_gap_count: number;
  progress_percentage?: number; // Progress as percentage
  contract_id: number;
  contract_number: string;
  contract_name: string;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  contract_amount?: number | null;
  contract_type?: string;
  cost_center?: string | null;
  division_name?: string | null;
  division_lead_name?: string | null;
  contract_owners?: Array<{ id: number; name: string }>;
  contract_managers?: Array<{ id: number; name: string }>;
  original_assessment_date?: string | null;
  last_assessment_date?: string | null;
  assessment_year?: number | null;
  evidence_received_date?: string | null;
  gaps_remediated_count?: number;
  gaps_not_remediated_count?: number;
  supplier_id: number;
  supplier_name: string;
  tier_id: number;
  tier_name: string;
  tier_risk_level: string;
  assessor_id: number | null; // Single assessor assignment
  assessor_name: string | null;
  assessor_email?: string;
  // Set when the assessment is cancelled; null on legacy Cancelled rows (no backfill)
  cancellation_reason?: string | null;
  cancelled_at?: string | null;
  cancelled_by_id?: number | null;
  cancelled_by_name?: string | null;
  conflicts?: Array<{
    id: number;
    conflict_type_code: string;
    conflict_type_name: string;
    entity_type: string;
    entity_id: number;
    status: 'open' | 'acknowledged' | 'resolved';
    severity: 'info' | 'warning' | 'error' | 'blocking';
    context: Record<string, unknown>;
    detected_at: string;
  }>;
}

export type AssessmentStatus =
  | 'Pending'
  | 'Scheduled'
  | 'Rescheduled'
  | 'Pending Evidence'
  | 'Evidence Past Due'
  | 'Escalated'
  | 'Final'
  | 'Cancelled'
  | 'Expired'
  | 'Not Scheduled';

/** Published assessments still in progress (the Active status group). */
export const ACTIVE_PUBLISHED_STATUSES: AssessmentStatus[] = ['Pending Evidence', 'Evidence Past Due', 'Escalated'];

/** Statuses an assessment can be cancelled from (by some role). */
export const CANCELLABLE_STATUSES: AssessmentStatus[] = [
  'Scheduled',
  'Rescheduled',
  'Pending',
  ...ACTIVE_PUBLISHED_STATUSES
];

/** Cancellation reason bounds, counted after trim (mirrors the backend). */
export const CANCELLATION_REASON_MIN_LENGTH = 10;
export const CANCELLATION_REASON_MAX_LENGTH = 1000;

// Assessment creation/update types
export interface CreateAssessmentRequest {
  name: string;
  contract_id: number;
  assessor_id: number;
  status?: AssessmentStatus;
  scheduled_date?: string;
  notes?: string;
}

export interface CreateAssessmentResponse {
  type: 'contract' | 'group';
  itemId: number;
  total_contracts: number;
  created: number;
  skipped: number;
  created_assessments: Array<{
    assessment_id: number;
    assessment_name: string;
    contract_id: number;
    contract_name: string;
    items_generated: number;
  }>;
  skipped_contracts: Array<{
    contract_id: number;
    contract_name: string;
    reason: string;
  }>;
  errors?: Array<{
    contract_id: number;
    error: string;
  }>;
}

export interface UpdateAssessmentRequest {
  name?: string;
  assessor_id?: number | null; // For reassignment and unassignment
  scheduled_date?: string;
  completed_date?: string;
  notes?: string;
}

// Assessment Items types
export interface AssessmentItem {
  id: number;
  assessment_id: number;
  process_id: number;
  evaluation: ItemEvaluation;
  notes: string;
  requires_evidence?: boolean; // Whether this process requires evidence
  created_at: string;
  updated_at: string;
  process_name: string;
  process_description: string;
  process_priority: ProcessPriority;
  process_type: ProcessType;
  domain_name: string;
  domain_description?: string;
  /** Aggregated evidence status from GET /assessments/:id/items. Defaults to none. */
  evidence_status?: EvidenceStatus;
  draft_count?: number;
  pending_count?: number;
  approved_count?: number;
}

export type ItemEvaluation =
  | 'Not Assessed'
  | 'Implemented'
  | 'Implemented (Needs Evidence)'
  | 'Gap'
  | 'Not Applicable'
  | 'Remediated';

export type ProcessPriority = 'High' | 'Medium' | 'Low';

/** Enterprise vs Operational process, from the assessment framework reference data. */
export const PROCESS_TYPES = ['ENT', 'OPS'] as const;

export type ProcessType = (typeof PROCESS_TYPES)[number];

/** Full names behind the ENT/OPS abbreviations (badge tooltip / accessible name). */
export const PROCESS_TYPE_LABELS: Record<ProcessType, string> = {
  ENT: 'Enterprise process',
  OPS: 'Operational process'
};

export interface AssessmentItemsResponse {
  assessment_id: number;
  assessment_name: string;
  assessment_status: AssessmentStatus;
  total_items: number;
  items: AssessmentItem[];
}

export interface UpdateAssessmentItemRequest {
  evaluation: ItemEvaluation;
  notes?: string;
}

export interface UpdateAssessmentItemResponse {
  message: string;
  item: AssessmentItem;
  gap_counts: {
    high_gaps: number;
    medium_gaps: number;
    low_gaps: number;
    total_gap_count: number;
  };
}
